import { memo } from "react";
import {
  EGG_BOTTOM,
  EGG_PATH,
  EGG_SVG_HEIGHT,
  EGG_SVG_WIDTH,
  EGG_TOP,
  STRIPE_COLORS,
} from "../constants";

interface Props {
  progress: number;
  shaking: boolean;
  invalid: boolean;
}

// Generates a wavy diagonal band using cubic bezier curves.
// Coordinates are relative to the SVG viewBox (0 0 200 220).
function wavyStripePath(
  yOffset: number,
  height: number,
  amplitude: number = 10,
  waveLen: number = 80,
): string {
  let d = `M -60,${yOffset}`;
  for (let x = -60; x < 320; x += waveLen) {
    d += ` C ${x + waveLen / 3},${yOffset - amplitude} ${(2 * waveLen) / 3 + x},${yOffset + amplitude} ${x + waveLen},${yOffset}`;
  }
  d += ` L 320,${yOffset + height}`;
  for (let x = 320; x > -60; x -= waveLen) {
    d += ` C ${x - waveLen / 3},${yOffset + height + amplitude} ${x - (2 * waveLen) / 3},${yOffset + height - amplitude} ${x - waveLen},${yOffset + height}`;
  }
  d += " Z";
  return d;
}

function EggComponent({ progress, shaking, invalid }: Props) {
  const clipId = "eggFillClip";
  const eggH = EGG_BOTTOM - EGG_TOP;
  const fillH = (progress / 100) * eggH;
  const clipY = EGG_BOTTOM - fillH;

  const animation = shaking
    ? "eggShake 0.5s ease-in-out"
    : invalid
      ? "invalidShake 0.5s ease-in-out"
      : "none";

  const glowFilter = shaking
    ? "drop-shadow(0 0 20px #FFD600)"
    : invalid
      ? "drop-shadow(0 0 12px rgba(255,60,60,0.6))"
      : "drop-shadow(0 0 10px rgba(0,200,255,0.3))";

  return (
    <svg
      viewBox="0 0 200 220"
      width={EGG_SVG_WIDTH}
      height={EGG_SVG_HEIGHT}
      style={{
        filter: glowFilter,
        animation,
        transition: "filter 0.3s",
      }}
    >
      <defs>
        <clipPath id="eggShape">
          <path d={EGG_PATH} />
        </clipPath>
        <clipPath id={clipId}>
          <rect
            x="0"
            y={clipY}
            width="200"
            height={fillH + 5}
            style={{
              transition:
                "y 0.6s cubic-bezier(0.34,1.56,0.64,1), height 0.6s cubic-bezier(0.34,1.56,0.64,1)",
            }}
          />
        </clipPath>
        <radialGradient id="eggGlow" cx="35%" cy="30%" r="55%">
          <stop offset="0%" stopColor="rgba(255,255,255,0.5)" />
          <stop offset="100%" stopColor="rgba(255,255,255,0)" />
        </radialGradient>
      </defs>

      {/* Unpainted grey egg with wavy stripes */}
      <g clipPath="url(#eggShape)">
        <rect x="0" y="0" width="200" height="220" fill="#C0C0C0" />
        {[0, 1, 2, 3].map((i) => (
          <path
            key={i}
            d={wavyStripePath(i * 55 - 20, 28)}
            transform="rotate(-30 100 110)"
            fill="rgba(255,255,255,0.15)"
          />
        ))}
      </g>

      {/* Colored fill layer with wavy stripes */}
      <g clipPath={`url(#${clipId})`}>
        <g clipPath="url(#eggShape)">
          <rect x="0" y="0" width="200" height="220" fill="#FF3B3B" />
          {STRIPE_COLORS.map((color, i) => (
            <path
              key={i}
              d={wavyStripePath(i * 42 - 30, 28)}
              transform="rotate(-30 100 110)"
              fill={i % 2 === 0 ? color : i % 3 === 1 ? "#FFD600" : "#FF8C00"}
              opacity="0.9"
            />
          ))}
          <ellipse
            cx="75"
            cy="80"
            rx="22"
            ry="34"
            fill="url(#eggGlow)"
            opacity="0.6"
          />
        </g>
      </g>

      {/* Egg outline — invalidShake animation targets the whole SVG */}
      <path
        d={EGG_PATH}
        fill="none"
        stroke="rgba(255,255,255,0.25)"
        strokeWidth="2"
      />

      {/* Gloss overlay */}
      <g clipPath="url(#eggShape)">
        <ellipse
          cx="72"
          cy="75"
          rx="18"
          ry="26"
          fill="url(#eggGlow)"
          opacity="0.35"
        />
      </g>

      {/* Progress line at fill boundary */}
      {progress > 0 && progress < 100 && (
        <line
          x1="32"
          y1={clipY}
          x2="168"
          y2={clipY}
          stroke="rgba(255,255,255,0.5)"
          strokeWidth="1.5"
          strokeDasharray="4 3"
          style={{ transition: "y1 0.6s, y2 0.6s" }}
        />
      )}
    </svg>
  );
}

export const Egg = memo(EggComponent);
export default Egg;
